// @safetyqube/sq-ai-client — Socket.io transport.
//
// Relocated from the testing UI's sqAiSocket.js (Phase 2). Same handshake shape
// (token, env, uiMode, confidentialAccess, userTz, devSql, webSearch) the /ai-assistant
// namespace middleware validates. Two changes for headless reuse, behaviour
// preserved: it is a FACTORY (one instance per caller) instead of a module
// singleton, and the JWT is read via an injected `getToken` (defaults to
// localStorage so the browser behaviour is identical) instead of touching
// localStorage directly. A `logger` is injectable (defaults to console).

import { io } from "socket.io-client";

const defaultGetToken = () => {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem("jwt_token") : null;
  } catch (_) {
    return null;
  }
};

const consoleLogger =
  typeof console !== "undefined" ? console : { log() {}, error() {} };

export function createSqAiTransport({
  namespace = "/ai-assistant",
  // Origin of the sq-ai server. Empty = same-origin (the testing UI, which is
  // served by that server / proxied to it). A DIFFERENT origin — a product
  // embedding the agent — passes its absolute URL; socket.io reads the host from
  // the front of the string and the namespace from its path, and `path` below
  // still points at the server's /socket.io endpoint either way.
  baseUrl = "",
  getToken = defaultGetToken,
  logger = consoleLogger,
  // Handshake defaults — match the testing UI's sqAiSocket.js. A production SQ
  // integration can override (e.g. devSql:false to never receive generated SQL).
  env = "dev",
  uiMode = "prod",
  confidentialAccess = false,
  userTz = "Asia/Calcutta",
  devSql = true,
  // Whether this caller ASKS for web search. Undefined by default and deliberately not a
  // boolean: the engine's ceiling lives in the environment (webSearchAccess.js) and this
  // value may only narrow it, so an absent field means "no opinion, use the ceiling" while
  // an explicit false switches search off for the session. A host that never sets it — the
  // SQ app — gets whatever the ceiling says.
  webSearch,
  reconnectionAttempts = 5,
  reconnectionDelay = 1000,
} = {}) {
  let socket = null;
  // Mutable auth the caller stages via setAuth; refreshAuth rebuilds the
  // handshake from these on each reconnect.
  const current = { env, uiMode, confidentialAccess: confidentialAccess === true, userTz, devSql, webSearch };

  function buildAuth() {
    return {
      token: getToken(),
      env: current.env,
      uiMode: current.uiMode,
      confidentialAccess: current.confidentialAccess === true,
      userTz: current.userTz,
      devSql: current.devSql,
      // Left off the handshake entirely when unset — socket.io JSON-serializes the auth
      // object, so `undefined` would arrive absent anyway; omitting it says so on purpose.
      ...(current.webSearch == null ? {} : { webSearch: current.webSearch === true }),
    };
  }

  // "" + "/ai-assistant" → same-origin namespace (unchanged); a baseUrl is joined
  // without doubling the slash so "https://ai.example.com/" behaves like the bare host.
  const endpoint = baseUrl ? baseUrl.replace(/\/+$/, "") + namespace : namespace;

  function getSocket() {
    if (socket) return socket;
    socket = io(endpoint, {
      autoConnect: true,
      auth: buildAuth(),
      reconnection: true,
      reconnectionAttempts,
      reconnectionDelay,
    });
    socket.on("connect", () => logger.log("[sq-ai-socket] connected", socket.id));
    socket.on("connect_error", (err) => logger.error("[sq-ai-socket] connect_error:", err.message, err.data));
    socket.on("disconnect", (reason) => logger.log("[sq-ai-socket] disconnect:", reason));
    return socket;
  }

  // Stage the env / confidentialAccess / userTz / webSearch used on the next (re)connect.
  // Callers invoke refreshAuth() after a control changes to apply the handshake.
  function setAuth({ env, confidentialAccess, userTz, webSearch } = {}) {
    if (env != null) current.env = env;
    if (confidentialAccess != null) current.confidentialAccess = confidentialAccess === true;
    if (userTz != null) current.userTz = userTz;
    if (webSearch != null) current.webSearch = webSearch === true;
  }

  function refreshAuth() {
    if (!socket) return;
    socket.auth = buildAuth();
    socket.disconnect();
    socket.connect();
  }

  function disconnect() {
    if (!socket) return;
    socket.disconnect();
    socket = null;
  }

  return { getSocket, setAuth, refreshAuth, disconnect };
}
